/**
 * A stylised humanoid, built in code.
 *
 * Used for both the player and the crowd, so the neighbourhood is populated by
 * people that read the same way the player does. The old avatar was a capsule
 * with two unattached floating capsules for arms (VISUAL_AUDIT V-02); this has
 * a proper skeleton hierarchy — shoulders parent upper arms parent forearms —
 * so a walk cycle can be driven by rotating joints rather than sliding boxes.
 *
 * Deliberately NOT a skinned mesh. Rigid parts on a joint hierarchy give a
 * clean toy-figure look that suits the art direction, cost nothing to author,
 * and avoid pulling in a glTF asset whose licence would need auditing.
 */
import * as THREE from 'three';
import { shade } from './Buildings.js';

export interface HumanoidParts {
  root: THREE.Group;
  /** Rotates independently of travel direction, for looking around. */
  head: THREE.Group;
  torso: THREE.Group;
  hips: THREE.Group;
  leftArm: THREE.Group;
  rightArm: THREE.Group;
  leftForearm: THREE.Group;
  rightForearm: THREE.Group;
  leftLeg: THREE.Group;
  rightLeg: THREE.Group;
  leftShin: THREE.Group;
  rightShin: THREE.Group;
  /** Ankles, so the foot can stay level through stance instead of pivoting. */
  leftFoot: THREE.Group;
  rightFoot: THREE.Group;
  /** Attach carried items here. */
  leftHand: THREE.Group;
  rightHand: THREE.Group;
  height: number;
  /** Hip height at rest, in local units. The animator bobs around this. */
  hipHeight: number;
}

export interface HumanoidStyle {
  skin: number;
  hair: number;
  shirt: number;
  trousers: number;
  shoes: number;
  /** Metres; 1.75 is the reference build. */
  height: number;
  hat?: number | null;
  /** Slightly different builds stop a crowd looking cloned. */
  bulk?: number;
}

export const SKIN_TONES = [
  0xf0c8a0, 0xe0ab84, 0xc68863, 0xa06a45, 0x7a4f32, 0x5c3a24, 0xf5d5b8,
];
export const HAIR_COLOURS = [
  0x2b1d14, 0x4a3220, 0x6b4a2a, 0x9c6b3f, 0xc9a227, 0x8a8a8a, 0x1a1a1a, 0xb5502a,
];
export const SHIRT_COLOURS = [
  0x2f6f4f, 0x3a5f8a, 0x8a3f3f, 0x6b4a8a, 0xb5762a, 0x37806f, 0x8a7a3a,
  0x4a4f5a, 0xa8546b, 0x2f5f7a, 0x7a5a3a, 0x5a7a3a,
];
export const TROUSER_COLOURS = [
  0x33405c, 0x3a3a42, 0x4a4030, 0x2b3a4a, 0x5a4a3f, 0x38484a,
];

/**
 * Build one figure. Everything is parented so that rotating a joint moves the
 * limb below it, which is what makes the animation code short and believable.
 */
export function buildHumanoid(style: HumanoidStyle): HumanoidParts {
  const bulk = style.bulk ?? 1;

  const skinMat = mat(style.skin, 0.75);
  const hairMat = mat(style.hair, 0.85);
  const shirtMat = mat(style.shirt, 0.88);
  const trouserMat = mat(style.trousers, 0.9);
  const shoeMat = mat(style.shoes, 0.6);

  const root = new THREE.Group();

  // --- hips ----------------------------------------------------------------
  // Proportions follow a real figure rather than a toy one
  // (VISUAL_REALISM_AUDIT R-09). The previous build measured 6.3 heads tall
  // with the hip joint at 42% of stature; an adult is about 7.2 heads with the
  // hip at 46-47%. A big head on short legs is the single loudest "this is a
  // game asset" signal, and it read at every distance the camera uses.
  const hips = new THREE.Group();
  hips.position.y = HIP_HEIGHT;
  root.add(hips);

  const pelvis = new THREE.Mesh(rounded(0.32 * bulk, 0.22, 0.21 * bulk), trouserMat);
  pelvis.castShadow = true;
  hips.add(pelvis);

  // A belt. One dark band at the waist is what stops shirt and trousers reading
  // as one two-tone column at gameplay distance.
  const belt = new THREE.Mesh(
    rounded(0.335 * bulk, 0.055, 0.225 * bulk),
    mat(shade(style.trousers, -0.45), 0.55),
  );
  belt.position.y = 0.1;
  hips.add(belt);

  // --- torso ---------------------------------------------------------------
  const torso = new THREE.Group();
  torso.position.y = 0.1;
  hips.add(torso);

  const chest = new THREE.Mesh(rounded(0.4 * bulk, 0.44, 0.235 * bulk), shirtMat);
  chest.position.y = 0.24;
  chest.castShadow = true;
  torso.add(chest);

  // A real neck, not a collar strip. With the head this much smaller the gap
  // between shoulders and skull is what sells the proportion, so it has to be
  // a column of the right thickness rather than a spacer.
  const neck = new THREE.Mesh(rounded(0.135, 0.12, 0.13), skinMat);
  neck.position.y = 0.5;
  torso.add(neck);
  const collar = new THREE.Mesh(rounded(0.3 * bulk, 0.05, 0.215 * bulk), mat(shade(style.shirt, 0.12), 0.9));
  collar.position.y = 0.45;
  torso.add(collar);

  // --- head ----------------------------------------------------------------
  const head = new THREE.Group();
  head.position.y = 0.555;
  torso.add(head);

  const skull = new THREE.Mesh(rounded(0.215, 0.245, 0.225), skinMat);
  skull.position.y = 0.125;
  skull.castShadow = true;
  head.add(skull);

  // A jaw, narrower than the cranium. On the old ovoid the head was one
  // rounded block; at this size the taper is most of what stops it reading
  // as a ball on a stick.
  const jaw = new THREE.Mesh(rounded(0.175, 0.085, 0.195), skinMat);
  jaw.position.set(0, 0.045, 0.012);
  head.add(jaw);

  // Hair as a cap over the skull, with a slight forward fringe.
  const hair = new THREE.Mesh(rounded(0.228, 0.135, 0.235), hairMat);
  hair.position.set(0, 0.2, -0.008);
  head.add(hair);
  const fringe = new THREE.Mesh(rounded(0.215, 0.055, 0.075), hairMat);
  fringe.position.set(0, 0.163, 0.088);
  head.add(fringe);

  // Eyes: two dark dots. Tiny, but a face reads as a person and a blank ovoid
  // reads as a mannequin.
  const eyeMat = mat(0x1a1a22, 0.4);
  for (const x of [-0.058, 0.058]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.023, 8, 6), eyeMat);
    eye.position.set(x, 0.126, 0.112);
    head.add(eye);
  }

  if (style.hat != null) {
    const hatMat = mat(style.hat, 0.7);
    const crown = new THREE.Mesh(rounded(0.238, 0.115, 0.242), hatMat);
    crown.position.y = 0.222;
    crown.castShadow = true;
    head.add(crown);
    const peak = new THREE.Mesh(rounded(0.222, 0.03, 0.14), hatMat);
    peak.position.set(0, 0.183, 0.15);
    head.add(peak);
  }

  // --- arms ----------------------------------------------------------------
  function buildArm(side: -1 | 1) {
    const shoulder = new THREE.Group();
    shoulder.position.set(side * 0.2 * bulk, 0.4, 0);
    torso.add(shoulder);

    // A deltoid cap over the shoulder joint. Without it the narrower arm
    // leaves a visible notch where it meets the chest.
    const deltoid = new THREE.Mesh(rounded(0.145 * bulk, 0.14, 0.16 * bulk), shirtMat);
    deltoid.position.y = -0.03;
    shoulder.add(deltoid);

    const upper = new THREE.Mesh(rounded(0.115, 0.3, 0.12), shirtMat);
    upper.position.y = -0.15;
    upper.castShadow = true;
    shoulder.add(upper);

    const elbow = new THREE.Group();
    elbow.position.y = -0.3;
    shoulder.add(elbow);

    const forearm = new THREE.Mesh(rounded(0.1, 0.28, 0.1), skinMat);
    forearm.position.y = -0.14;
    forearm.castShadow = true;
    elbow.add(forearm);

    const hand = new THREE.Group();
    hand.position.y = -0.28;
    elbow.add(hand);
    const fist = new THREE.Mesh(rounded(0.095, 0.115, 0.085), skinMat);
    hand.add(fist);

    return { shoulder, elbow, hand };
  }
  const left = buildArm(-1);
  const right = buildArm(1);

  // --- legs ----------------------------------------------------------------
  function buildLeg(side: -1 | 1) {
    const hip = new THREE.Group();
    hip.position.set(side * 0.105 * bulk, -0.1, 0);
    hips.add(hip);

    const thigh = new THREE.Mesh(rounded(0.155, 0.41, 0.16), trouserMat);
    thigh.position.y = -0.205;
    thigh.castShadow = true;
    hip.add(thigh);

    const knee = new THREE.Group();
    knee.position.y = -0.41;
    hip.add(knee);

    const shin = new THREE.Mesh(rounded(0.13, 0.4, 0.135), trouserMat);
    shin.position.y = -0.2;
    shin.castShadow = true;
    knee.add(shin);

    // The foot hangs off an ankle joint rather than off the shin. A rigid
    // foot swings with the whole leg, which is why the old walk read as a
    // marionette: real feet stay flat on the ground through the stance half
    // of the stride and only pivot at the ends.
    const ankle = new THREE.Group();
    ankle.position.y = -0.4;
    knee.add(ankle);

    const foot = new THREE.Mesh(rounded(0.145, 0.095, 0.265), shoeMat);
    foot.position.set(0, -0.0425, 0.045);
    foot.castShadow = true;
    ankle.add(foot);

    // A sole, a shade darker: it sharpens where the figure meets the ground,
    // which is the contact the eye checks first.
    const sole = new THREE.Mesh(
      rounded(0.155, 0.035, 0.285),
      mat(shade(style.shoes, -0.4), 0.85),
    );
    sole.position.set(0, -0.0925, 0.045);
    ankle.add(sole);

    return { hip, knee, ankle };
  }
  const leftLegParts = buildLeg(-1);
  const rightLegParts = buildLeg(1);

  // Scale from what the figure actually measures, not from a constant that
  // drifts every time a limb changes length. The old divisor claimed a 1.75 m
  // reference build; the geometry it scaled was 1.93 m and its feet sat below
  // the ground plane, so every figure in the game was 10% too tall and very
  // slightly sunk.
  root.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(root);
  hips.position.y -= bounds.min.y;
  root.scale.setScalar(style.height / (bounds.max.y - bounds.min.y));
  const hipHeight = hips.position.y;

  return {
    root, head, torso, hips, hipHeight,
    leftArm: left.shoulder, rightArm: right.shoulder,
    leftForearm: left.elbow, rightForearm: right.elbow,
    leftHand: left.hand, rightHand: right.hand,
    leftLeg: leftLegParts.hip, rightLeg: rightLegParts.hip,
    leftShin: leftLegParts.knee, rightShin: rightLegParts.knee,
    leftFoot: leftLegParts.ankle, rightFoot: rightLegParts.ankle,
    height: style.height,
  };
}

/** Hip-joint height in the unscaled build, before the fit-to-height pass. */
const HIP_HEIGHT = 0.985;

/**
 * Procedural animation state for one figure.
 *
 * There are no keyframes and no skinning: gaits are sine waves on joint
 * rotations, phase-locked to distance travelled so the feet do not skate.
 */
export class HumanoidAnimator {
  private phase = 0;
  private blend = 0;
  private breathe = 0;
  private lean = 0;
  // Idle has its own slow clock and its own scattered glances, seeded per
  // figure so a crowd does not shift its weight in unison.
  private idleClock = Math.random() * 40;
  private glanceAt = 4 + Math.random() * 9;
  private glance = 0;
  private glanceTarget = 0;

  constructor(private readonly parts: HumanoidParts) {}

  /**
   * @param speed metres per second the figure is actually travelling
   * @param running whether the run gait should be used
   * @param turnRate radians per second, drives the lean into corners
   * @param mode overrides the gait entirely
   */
  update(
    dt: number,
    speed: number,
    running: boolean,
    turnRate = 0,
    mode: 'locomotion' | 'working' | 'sitting' = 'locomotion',
  ): void {
    const p = this.parts;
    this.breathe += dt * 1.6;

    if (mode === 'working') {
      this.animateWorking(dt);
      return;
    }

    // Stride length grows with speed, so the cycle stays locked to the ground.
    const strideLength = running ? 2.1 : 1.5;
    this.phase += (speed / strideLength) * Math.PI * 2 * dt;
    const moving = speed > 0.15;
    this.blend += ((moving ? 1 : 0) - this.blend) * Math.min(1, dt * 9);

    const amp = (running ? 1.35 : 1) * this.blend;
    const swing = Math.sin(this.phase);
    const counter = Math.sin(this.phase + Math.PI);

    // Legs
    p.leftLeg.rotation.x = swing * 0.62 * amp;
    p.rightLeg.rotation.x = counter * 0.62 * amp;
    // Knees only bend backwards, and mostly on the recovery half of the stride.
    p.leftShin.rotation.x = Math.max(0, -swing) * 0.95 * amp;
    p.rightShin.rotation.x = Math.max(0, -counter) * 0.95 * amp;

    // Ankles hold the foot near level instead of letting it swing with the
    // leg, then let it drop through toe-off. Clamped, because an ankle that
    // fully cancels the leg looks hinged rather than jointed.
    for (const [leg, shin, foot] of [
      [p.leftLeg, p.leftShin, p.leftFoot],
      [p.rightLeg, p.rightShin, p.rightFoot],
    ] as const) {
      foot.rotation.x = THREE.MathUtils.clamp(
        -(leg.rotation.x + shin.rotation.x) * 0.7, -0.45, 0.5,
      );
    }

    // Arms swing opposite the legs, with a permanent slight bend at the elbow.
    p.leftArm.rotation.x = counter * 0.55 * amp;
    p.rightArm.rotation.x = swing * 0.55 * amp;
    p.leftArm.rotation.z = 0.09 + (running ? 0.06 : 0) * amp;
    p.rightArm.rotation.z = -0.09 - (running ? 0.06 : 0) * amp;
    p.leftForearm.rotation.x = -0.22 - Math.max(0, counter) * 0.5 * amp;
    p.rightForearm.rotation.x = -0.22 - Math.max(0, swing) * 0.5 * amp;

    // Vertical bob, twice per stride, plus an idle breath when standing.
    const bob = Math.abs(Math.sin(this.phase)) * (running ? 0.075 : 0.045) * amp;
    const breath = (1 - this.blend) * Math.sin(this.breathe) * 0.008;

    // Idle: a slow weight shift from one leg to the other, and a glance every
    // several seconds. A figure that only breathes reads as paused, which is
    // exactly how the old standing pose looked whenever the player stopped.
    this.idleClock += dt;
    const rest = 1 - this.blend;
    const shift = Math.sin(this.idleClock * 0.42) * rest;
    p.hips.position.y = p.hipHeight + bob + breath - Math.abs(shift) * 0.012;
    p.hips.rotation.z = shift * 0.045;
    p.hips.position.x = shift * 0.022;

    if (this.idleClock > this.glanceAt) {
      this.glanceAt = this.idleClock + 4 + Math.random() * 9;
      this.glanceTarget = (Math.random() - 0.5) * 0.9;
    }
    // Hold the glance for a moment, then drift back to centre.
    this.glance += ((rest > 0.5 ? this.glanceTarget : 0) - this.glance)
      * Math.min(1, dt * 2.2);

    // Counter-rotation through the spine, and a forward pitch when running.
    // The pelvis turns the other way: shoulders and hips rotating together is
    // the difference between a walk and a shop mannequin on a turntable.
    p.torso.rotation.y = -Math.sin(this.phase) * 0.14 * amp;
    p.hips.rotation.y = Math.sin(this.phase) * 0.09 * amp;
    p.torso.rotation.x = (running ? 0.16 : 0.045) * this.blend;

    // Lean into turns. Eased, because snapping the lean looks like a glitch.
    const targetLean = THREE.MathUtils.clamp(-turnRate * 0.16, -0.28, 0.28) * this.blend;
    this.lean += (targetLean - this.lean) * Math.min(1, dt * 6);
    p.torso.rotation.z = this.lean;

    // The head stays level and looks slightly into the turn.
    p.head.rotation.z = -this.lean * 0.5;
    p.head.rotation.y = THREE.MathUtils.clamp(-turnRate * 0.1, -0.3, 0.3) + this.glance;
    p.head.rotation.x = -p.torso.rotation.x * 0.6 + rest * Math.sin(this.breathe * 0.5) * 0.02;
  }

  /** Pushing a mower: leaning forward, arms out, small effortful bounce. */
  private animateWorking(dt: number): void {
    const p = this.parts;
    this.phase += dt * 4.4;
    const push = Math.sin(this.phase);

    p.hips.position.y = p.hipHeight + Math.abs(push) * 0.02;
    p.hips.position.x = 0;
    p.hips.rotation.set(0, 0, 0);
    p.leftFoot.rotation.x = 0;
    p.rightFoot.rotation.x = 0;
    p.torso.rotation.x = 0.3 + push * 0.05;
    p.torso.rotation.y = 0;
    p.torso.rotation.z = 0;

    p.leftArm.rotation.x = -0.95 + push * 0.1;
    p.rightArm.rotation.x = -0.95 - push * 0.1;
    p.leftArm.rotation.z = 0.2;
    p.rightArm.rotation.z = -0.2;
    p.leftForearm.rotation.x = -0.25;
    p.rightForearm.rotation.x = -0.25;

    // Short shuffling steps rather than a stride.
    p.leftLeg.rotation.x = push * 0.22;
    p.rightLeg.rotation.x = -push * 0.22;
    p.leftShin.rotation.x = Math.max(0, -push) * 0.3;
    p.rightShin.rotation.x = Math.max(0, push) * 0.3;

    p.head.rotation.x = -0.18;
    p.head.rotation.y = 0;
    p.head.rotation.z = 0;
  }
}

/** Rounded box: the toy-figure look, and it catches light far better than a cube. */
function rounded(width: number, height: number, depth: number): THREE.BufferGeometry {
  // Segment counts kept low; these are drawn dozens of times.
  const geometry = new THREE.BoxGeometry(width, height, depth, 2, 2, 2);
  const position = geometry.attributes.position as THREE.BufferAttribute;
  const half = new THREE.Vector3(width / 2, height / 2, depth / 2);
  const radius = Math.min(width, height, depth) * 0.3;
  const v = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    v.fromBufferAttribute(position, i);
    // Push each vertex toward the inner box, then back out along its normal —
    // a cheap way to round the corners without a CSG library.
    const inner = new THREE.Vector3(
      THREE.MathUtils.clamp(v.x, -(half.x - radius), half.x - radius),
      THREE.MathUtils.clamp(v.y, -(half.y - radius), half.y - radius),
      THREE.MathUtils.clamp(v.z, -(half.z - radius), half.z - radius),
    );
    const offset = v.clone().sub(inner);
    if (offset.lengthSq() > 1e-8) {
      offset.setLength(radius);
      v.copy(inner).add(offset);
      position.setXYZ(i, v.x, v.y, v.z);
    }
  }
  geometry.computeVertexNormals();
  return geometry;
}

function mat(colour: number, roughness: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: colour, roughness, metalness: 0 });
}
